import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, Save, Send, Wifi, RefreshCcw, AlertTriangle,
  Settings2, TestTube2, HistoryIcon, Trash2, CheckCircle2, WifiOff, XCircle,
  MessageSquare, Sparkles, Workflow, Landmark, MapPin, Mail, Plug2,
} from "lucide-react";

const ICON_MAP = {
  "message-square": MessageSquare, "sparkles": Sparkles, "workflow": Workflow,
  "landmark": Landmark, "map-pin": MapPin, "mail": Mail,
};
const STATUS_META = {
  not_configured: { label: "Não configurado", cls: "text-[#737373] border-[#3a3a3a] bg-[#0a0a0a]" },
  configured:     { label: "Configurado",     cls: "text-cyan-300 border-cyan-500/40 bg-cyan-500/10" },
  connected:      { label: "Conectado",       cls: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10" },
  disconnected:   { label: "Desconectado",    cls: "text-amber-300 border-amber-500/40 bg-amber-500/10" },
  error:          { label: "Erro",            cls: "text-red-300 border-red-500/40 bg-red-500/10" },
};

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("pt-BR");
}

export default function IntegracaoDetailPage() {
  const { code } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("config");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/integrations/${code}`);
      setData(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Integração não encontrada");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [code]);

  if (loading || !data) {
    return (
      <Layout>
        <div className="fc-card p-10 text-center text-[#737373]">
          <Loader2 className="animate-spin mx-auto" size={20}/>
        </div>
      </Layout>
    );
  }

  const Icon = ICON_MAP[data.icon] || Plug2;
  const st = STATUS_META[data.status] || STATUS_META.not_configured;

  return (
    <Layout>
      <div className="mb-6">
        <Link to="/integracoes" className="inline-flex items-center gap-1.5 text-[12px] text-[#A3A3A3] hover:text-[#D4AF37]" data-testid="back-link">
          <ArrowLeft size={14}/> Todas integrações
        </Link>
      </div>

      <div className="mb-6 flex items-start gap-4">
        <div className="flex-shrink-0 w-14 h-14 rounded-md border border-emerald-500/30 bg-emerald-500/5 flex items-center justify-center text-emerald-300">
          <Icon size={26}/>
        </div>
        <div className="flex-1">
          <div className="text-[11px] uppercase tracking-[0.2em] text-emerald-300">Integração</div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mt-1 flex items-center gap-3" data-testid="integ-title">
            {data.name}
            <span className={`text-[11px] px-2.5 py-1 rounded border ${st.cls}`} data-testid="integ-status">{st.label}</span>
          </h1>
          <p className="text-[#A3A3A3] text-[13px] mt-1 max-w-2xl">{data.description}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-5 border-b border-[#1a1a1a]" data-testid="integ-tabs">
        {[
          { v: "config", l: "Configuração", icon: Settings2 },
          { v: "test", l: "Teste rápido", icon: TestTube2 },
          { v: "history", l: "Histórico de testes", icon: HistoryIcon },
          { v: "errors", l: "Logs de erro", icon: AlertTriangle },
        ].map(t => {
          const TIcon = t.icon;
          return (
            <button
              key={t.v}
              onClick={() => setTab(t.v)}
              className={`px-3.5 py-2 text-[12.5px] flex items-center gap-2 border-b-2 transition-colors ${tab === t.v ? "border-emerald-400 text-emerald-300" : "border-transparent text-[#A3A3A3] hover:text-[#E5E5E5]"}`}
              data-testid={`tab-${t.v}`}
            >
              <TIcon size={13}/> {t.l}
            </button>
          );
        })}
      </div>

      {tab === "config"  && <ConfigTab data={data} onReload={load}/>}
      {tab === "test"    && <TestTab data={data} onReload={load}/>}
      {tab === "history" && <HistoryTab code={code}/>}
      {tab === "errors"  && <ErrorsTab code={code}/>}
    </Layout>
  );
}

// -------- CONFIG TAB --------
function ConfigTab({ data, onReload }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    // pré-popula com valores atuais (não-sensíveis) — sensíveis ficam vazios (só máscara é retornada)
    const initial = {};
    Object.entries(data.config || {}).forEach(([k, v]) => {
      if (k.endsWith("_masked")) return;
      if (data.sensitive_fields.includes(k)) {
        initial[k] = ""; // usuário digita se quiser trocar
      } else {
        initial[k] = v ?? "";
      }
    });
    setForm(initial);
  }, [data]);

  const save = async () => {
    setSaving(true);
    try {
      // remove sensíveis vazios (backend preserva o antigo)
      const payload = { ...form };
      data.sensitive_fields.forEach(f => { if (!payload[f]) delete payload[f]; });
      await api.put(`/integrations/${data.code}/configure`, { config: payload });
      toast.success("Configuração salva");
      onReload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao salvar");
    } finally { setSaving(false); }
  };

  const verify = async () => {
    setVerifying(true);
    try {
      const { data: r } = await api.post(`/integrations/${data.code}/verify-connection`);
      if (r.state === "connected") toast.success("Conectado!");
      else if (r.state === "waiting_qr") toast.info("Aguardando QR code");
      else if (r.state === "disconnected") toast.warning("Desconectado");
      else if (r.state === "not_implemented") toast.info("Adapter ainda não implementado");
      else toast.error(r.error || `Estado: ${r.state}`);
      onReload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha");
    } finally { setVerifying(false); }
  };

  const fields = Object.keys(data.field_labels || {});
  return (
    <div className="fc-card p-5 max-w-2xl" data-testid="config-form">
      <div className="text-[11px] uppercase tracking-wider text-[#737373] mb-3">Credenciais e endpoints</div>
      <div className="grid gap-3">
        {fields.map(k => {
          const isSensitive = data.sensitive_fields.includes(k);
          const isRequired = data.required_fields.includes(k);
          const currentMasked = data.config?.[isSensitive ? `${k}_masked` : k];
          return (
            <label key={k} className="block">
              <div className="text-[11px] text-[#A3A3A3] mb-1">
                {data.field_labels[k]} {isRequired && <span className="text-red-300">*</span>}
              </div>
              <input
                type={isSensitive ? "password" : "text"}
                className="fc-input text-[13px] font-mono-tab"
                value={form[k] ?? ""}
                onChange={e => setForm({ ...form, [k]: e.target.value })}
                placeholder={data.field_placeholders?.[k] || ""}
                data-testid={`field-${k}`}
              />
              {isSensitive && currentMasked && (
                <div className="text-[10.5px] text-[#737373] mt-1">
                  Valor atual (mascarado): <span className="font-mono-tab text-emerald-300/80">{currentMasked}</span>
                  {" · deixe vazio para manter"}
                </div>
              )}
            </label>
          );
        })}
      </div>
      <div className="flex gap-2 mt-5">
        <button onClick={save} disabled={saving} className="fc-btn-primary text-sm flex items-center gap-2" data-testid="btn-save-config">
          {saving ? <Loader2 size={13} className="animate-spin"/> : <Save size={13}/>} Salvar configuração
        </button>
        <button onClick={verify} disabled={verifying} className="fc-btn-ghost text-sm flex items-center gap-2" data-testid="btn-verify-conn">
          {verifying ? <Loader2 size={13} className="animate-spin"/> : <Wifi size={13}/>} Verificar conexão
        </button>
      </div>
      {!data.implemented && (
        <div className="mt-4 p-3 rounded-md border border-amber-500/30 bg-amber-500/5 text-[12px] text-amber-200">
          ⚠️ Adapter ainda não implementado. Você pode salvar as credenciais (elas ficam criptografadas no banco),
          mas os botões <em>Testar</em> e <em>Verificar conexão</em> só ficarão ativos quando o adapter for plugado.
        </div>
      )}
    </div>
  );
}

// -------- TEST TAB --------
function TestTab({ data, onReload }) {
  const [number, setNumber] = useState("");
  const [text, setText] = useState("Mensagem de teste — Painel Forte Capital");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const isWhatsApp = data.code === "whatsapp_evolution";

  const runTest = async () => {
    setSending(true); setResult(null);
    try {
      const payload = isWhatsApp
        ? { number: number.replace(/\D/g, ""), text }
        : {};
      const { data: r } = await api.post(`/integrations/${data.code}/test`, payload);
      setResult(r);
      if (r.not_implemented) toast.info("Adapter ainda não implementado");
      else if (r.ok) toast.success("Teste OK");
      else toast.error(r.error || "Falha no teste");
      onReload();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Erro";
      setResult({ ok: false, error: detail });
      toast.error(detail);
    } finally { setSending(false); }
  };

  return (
    <div className="fc-card p-5 max-w-2xl" data-testid="test-form">
      {isWhatsApp ? (
        <>
          <div className="text-[11px] uppercase tracking-wider text-emerald-300 mb-3">Envio de mensagem</div>
          <label className="block mb-3">
            <div className="text-[11px] text-[#A3A3A3] mb-1">Número (DDI+DDD+número)</div>
            <input type="tel" className="fc-input font-mono-tab" value={number} onChange={e=>setNumber(e.target.value)} placeholder="5511987654321" data-testid="test-number"/>
          </label>
          <label className="block mb-4">
            <div className="text-[11px] text-[#A3A3A3] mb-1">Mensagem</div>
            <textarea rows={4} className="fc-input text-[13px]" value={text} onChange={e=>setText(e.target.value)} data-testid="test-text"/>
          </label>
        </>
      ) : (
        <div className="mb-4 p-3 rounded-md border border-[#1f1f1f] bg-[#0a0a0a] text-[12.5px] text-[#A3A3A3]">
          Esta integração ainda não implementa teste específico. O botão abaixo apenas valida se o adapter existe.
        </div>
      )}
      <button
        onClick={runTest}
        disabled={sending || data.status === "not_configured" || (isWhatsApp && (!number || !text))}
        className="fc-btn-primary text-sm flex items-center gap-2"
        data-testid="btn-run-test"
      >
        {sending ? <Loader2 size={13} className="animate-spin"/> : <Send size={13}/>}
        {isWhatsApp ? "Enviar WhatsApp de teste" : "Executar teste"}
      </button>

      {result && (
        <div
          className={`mt-4 p-3 rounded-md border text-[12.5px] ${
            result.ok ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-200"
                      : "border-red-500/40 bg-red-500/10 text-red-200"
          }`}
          data-testid="test-result"
        >
          <div className="font-medium flex items-center gap-2">
            {result.ok ? <CheckCircle2 size={14}/> : <XCircle size={14}/>}
            {result.ok ? "Sucesso" : (result.error || "Falha")}
          </div>
          {result.provider_id && (
            <div className="text-[11px] font-mono-tab text-[#A3A3A3] mt-1">
              ID Evolution: <span className="text-[#E5E5E5]">{result.provider_id}</span>
            </div>
          )}
          {result.raw && (
            <details className="mt-2">
              <summary className="text-[10.5px] text-[#737373] cursor-pointer">Resposta bruta</summary>
              <pre className="text-[10px] mt-1 whitespace-pre-wrap text-[#A3A3A3] max-h-40 overflow-auto">{JSON.stringify(result.raw, null, 2)}</pre>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

// -------- HISTORY TAB --------
function HistoryTab({ code }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    api.get(`/integrations/${code}/test-history`).then(r => setItems(r.data)).catch(()=>{}).finally(() => setLoading(false));
  }, [code]);
  if (loading) return <div className="fc-card p-8 text-center"><Loader2 className="animate-spin mx-auto" size={16}/></div>;
  if (!items.length) return <div className="fc-card p-10 text-center text-[#737373]" data-testid="history-empty">Sem histórico de testes ainda.</div>;
  return (
    <div className="fc-card p-0 overflow-hidden" data-testid="history-list">
      <table className="w-full text-[12.5px]">
        <thead className="bg-[#0f0f0f] text-[10.5px] uppercase tracking-wider text-[#737373]">
          <tr>
            <th className="text-left px-3 py-2">Data/Hora</th>
            <th className="text-left px-3 py-2">Tipo</th>
            <th className="text-left px-3 py-2">Resultado</th>
            <th className="text-left px-3 py-2">Número</th>
            <th className="text-left px-3 py-2">ID Provider</th>
            <th className="text-left px-3 py-2">Erro</th>
          </tr>
        </thead>
        <tbody>
          {items.map((h, idx) => (
            <tr key={idx} className="border-t border-[#1a1a1a]">
              <td className="px-3 py-2 font-mono-tab text-[11px]">{fmtDate(h.at)}</td>
              <td className="px-3 py-2 text-[#A3A3A3]">{h.detail?.kind || "—"}</td>
              <td className="px-3 py-2">
                {h.ok
                  ? <span className="text-emerald-300 flex items-center gap-1"><CheckCircle2 size={12}/> OK</span>
                  : <span className="text-red-300 flex items-center gap-1"><XCircle size={12}/> Falha</span>
                }
              </td>
              <td className="px-3 py-2 font-mono-tab text-[11px] text-[#A3A3A3]">{h.detail?.input?.number || "—"}</td>
              <td className="px-3 py-2 font-mono-tab text-[10.5px] text-[#737373]">{h.detail?.provider_id || "—"}</td>
              <td className="px-3 py-2 text-[11px] text-red-300/80">{h.detail?.error || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// -------- ERRORS TAB --------
function ErrorsTab({ code }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = () => {
    setLoading(true);
    api.get(`/integrations/${code}/error-logs`).then(r => setItems(r.data)).catch(()=>{}).finally(() => setLoading(false));
  };
  useEffect(load, [code]);

  const clear = async () => {
    if (!window.confirm("Limpar todos os logs de erro?")) return;
    await api.delete(`/integrations/${code}/error-logs`);
    toast.success("Logs limpos");
    load();
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-3">
        <div className="text-[12px] text-[#A3A3A3]">Total: {items.length}</div>
        {items.length > 0 && (
          <button onClick={clear} className="fc-btn-ghost text-xs flex items-center gap-1.5 text-red-300" data-testid="btn-clear-errors">
            <Trash2 size={12}/> Limpar
          </button>
        )}
      </div>
      {loading ? (
        <div className="fc-card p-8 text-center"><Loader2 className="animate-spin mx-auto" size={16}/></div>
      ) : !items.length ? (
        <div className="fc-card p-10 text-center text-[#737373]" data-testid="errors-empty">
          <CheckCircle2 size={28} className="mx-auto text-emerald-500/50 mb-2"/>
          Nenhum erro registrado.
        </div>
      ) : (
        <div className="grid gap-2" data-testid="errors-list">
          {items.map((e, idx) => (
            <div key={idx} className="fc-card p-3 border-l-2 border-red-500/60">
              <div className="flex items-start gap-3">
                <AlertTriangle size={14} className="text-red-300 flex-shrink-0 mt-0.5"/>
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] font-mono-tab text-[#737373]">{fmtDate(e.at)}</div>
                  <div className="text-[13px] text-red-200 mt-0.5 break-all">{e.message}</div>
                  {e.context && Object.keys(e.context).length > 0 && (
                    <details className="mt-1.5">
                      <summary className="text-[10.5px] text-[#737373] cursor-pointer">Contexto</summary>
                      <pre className="text-[10px] mt-1 whitespace-pre-wrap text-[#A3A3A3] max-h-32 overflow-auto">{JSON.stringify(e.context, null, 2)}</pre>
                    </details>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
